import type { FishState, ReefFish } from './types';
import {
  ageFish, classesOf, liveFish, monthStep, type AgeClass, type FishDraws, type FishJanuary, type FishLife, type FishSpeciesId, type FishSupply, type IslandDay, type IslandFishing,
} from './fish';
import { ROI_RATION_PER_DAY } from './data/fish/roiRation';
import { MOANOKEA_SURVEY } from './data/fish/moanokeaSurvey';

/**
 * Predation (M84, the user's word: "do predation analogous to the way safari
 * sim does it"): a predator's stocks eat its prey's, island by island, a day
 * at a time, as Sable Reserve's carnivores eat its herds
 * (`src/sim/systems/predation.ts` there), each part of its hunt carried over
 * to a reef:
 *
 * - **A day's need**: Sable's `dailyMeatKg` a head; here the predator's
 *   measured ration, the weight of food a kilogram of it eats a day, times
 *   its weight on the island.
 * - **Which prey it takes**: Sable weighs each herd by `preyPreference`,
 *   selection against availability from Hayward's Jacobs' indices, over the
 *   prey's mass. Here a prey fish's chance to be taken is weighed by its total
 *   length, the share of that length in the predator's stomachs over its
 *   share on the reef (`PREY_SIZE`).
 * - **How well it hunts**: Sable's success, D / (D + H + C), the prey met
 *   against a half-saturation and the rivals' demand. Here the share of its
 *   need it takes from the prey the game holds is A / G, at most all of it
 *   (`preyPart`, M128): A the prey on the island, each kilogram weighed by
 *   its size's chance, G the island's other prey (below). Each prey fish's
 *   chance of being eaten is then the predator's need over G, whatever the
 *   prey's own numbers: the predation is density independent in its prey,
 *   and their young's density dependence is the crowding's alone
 *   (since M255 each crowded stock's fitted to its steepness at the
 *   predation it meets, `FishLife.steepness`, so the predation is in its
 *   fit; review M267's: this still named the wrasse's measure, which no
 *   stock now uses; until then `CROWDING_M2_PER_FISH_DAY`, which was measured on natural reefs with
 *   their predators: "Researchers censused reefs daily ... for densities of
 *   ... (4) resident piscivores" (BCO-DMO 727143's description, p. 2; none
 *   removed). The young of reefs die the more the more of them there are
 *   where predators of both kinds hunt them, resident and transient (Hixon
 *   2015, in Mora's *Ecology of Fishes on Coral Reefs*, p. 46, of Hixon and
 *   Carr 1997, not served), and "predation is typically sporadic and density
 *   independent" where one kind hunts alone (ibid., Fig. 5.4's caption, p.
 *   48): the crowding's measure holds the first, so the predation here adds
 *   none of its own. Until M128 it was A / (A + G), Holling type II, each
 *   parrotfish's chance falling as they grew more: with the redlip the reef
 *   ran away from its balance in 16 of 32 drawn worlds within 200 years
 *   (MEASURED). Its type III form, A² / (A² + G²) ("For piscivores, nearly
 *   all responses were Holling type III", Smith and Smith 2020, *Fish and
 *   Fisheries* 21, p. 891, the northeast US shelf's), added a density
 *   dependence of its own to the crowding's: no balance held NOAA's density
 *   at the diets' share (M128, MEASURED: the roi's parrotfish 0.6% of its
 *   need at 196.5 days of it, 18.5% at 165.2 with the palenose's larvae
 *   9,953 a kilogram, the three tries between finding no balance in 400
 *   years).
 * - **The rest of the need**: Sable's small-prey larder (M307) meets a share
 *   of a carnivore's need from prey it does not count; here every fish of
 *   the reef the game does not hold (the roi's prey are 15 families,
 *   Dierking et al. 2009, Table 2), G, the same weight for an island day after
 *   day: as many weighed kilograms as a number of days of the island's
 *   predators' mean need, set by the balance (`scripts/reefFish.ts`) so that
 *   over the balance's year the parrotfish make up the share of the need the
 *   predator's stomachs give (`PredatorDiet.preyShare`).
 * - **Rivals**: Sable counts a rival's month of demand against each herd's
 *   hunt, its predators hunting apart. An island's fish of one species here
 *   are one stock that eats from one pool, so each eats what the others
 *   leave: the day's deaths are drawn from the pool they all share, and
 *   tomorrow's A is what is left. Since M126 several species hunt the same
 *   prey (the roi and the two jacks, the parrotfish), each apart as Sable's
 *   do: each on the prey as they stand that day, its chance its own
 *   (`huntRate`, its G its own), their chances on each prey class added (a prey fish's chance of
 *   being taken by one or another of them in the day), the day's deaths one
 *   draw.
 * - **The kill**: Sable takes whole animals, a stochastic rounding. Here the
 *   predation is a daily chance on each age class, drawn with its natural
 *   deaths and its fishing (`liveFish`), the deaths shared out by cause for
 *   the death audit (`audit.ts`): eaten, by each predator its part of the
 *   chance (`EatenBy`).
 *
 * Not carried: Sable's carnivores grow thin when they hunt badly, and breed
 * less. The other prey here are never eaten down (they are no stock), so a
 * predator is always fed; its condition, and a measured functional response
 * or interference (none was served: Harborne 2012's, the proxy refused), are
 * open (M84).
 */

/** A predator's diet: its prey stocks, its ration, the size of fish it takes, and the share of its need its prey make up at the balance. */
export interface PredatorDiet {
  predator: FishSpeciesId;
  prey: readonly FishSpeciesId[];
  /** The weight of food a kilogram of it eats a day, kg. */
  rationPerDay: number;
  /** The share of its need, by weight, its prey stocks make up over the balance's year. */
  preyShare: number;
  /** The stock whose table holds this hunt's other prey (`FishSupply.predatorOtherPreyKg`), where it is not the predator's own (a predator's hunt apart, on a prey of its own balance: the ulua's on the octopus, M129, the jacks' on the moano kea, M179). */
  otherPreyIn?: FishSpeciesId;
}

/**
 * The relative chance a prey fish is taken by its total length, by 5-cm
 * bin from 0 cm, the last bin every fish of 30 cm and more: the roi's
 * parrotfish prey's share in each bin over the parrotfishes' share in it on
 * Kona's reefs (Dierking, Williams and Walsh 2009, Fig. 5, its Scaridae
 * panel, "by %N"; the paper: "predation focused on the smaller size classes
 * present in the wild", p. 470). MEASURED, M84, by the figure's pixels at 600
 * dpi, its bars from its zero line against its ticks at 0.5 and 1 (398.5 px a
 * unit, less the zero line's half-width): in the stomachs 0.544, 0.273 and
 * 0.183 of 5 to 20 cm (6, 3 and 2 of the 11 measured, of 12 parrotfish
 * eaten, as 6/11, 3/11 and 2/11 give them), none under 5 cm or over 20; on the
 * reef 0.025, 0.122, 0.271, 0.291, 0.175, 0.067 and 0.042 (1,648 fish, the
 * seven summing to 0.993).
 */
const DIET_BY_SIZE = [0, 6 / 11, 3 / 11, 2 / 11, 0, 0, 0] as const;
const REEF_BY_SIZE = [0.025, 0.122, 0.271, 0.291, 0.175, 0.067, 0.042] as const;
export const PREY_SIZE_BIN_MM = 50;
export const PREY_SIZE: readonly number[] = DIET_BY_SIZE.map((d, k) => d / (REEF_BY_SIZE[k] as number));

/**
 * The roi's diet (M84). Its ration, the tanks' (`data/fish/roiRation.ts`).
 * Its prey the game holds: the parrotfish (the redlip with them since M128). Their share of its need: the
 * parrotfishes' share of its stomachs' prey by weight, 319.0 g of 1,154.9
 * (Scaridae 27.6% by mass, 285 roi of Kona and Oʻahu, July 2003; Dierking,
 * Williams and Walsh 2009, Table 2), times the game's parrotfish's share of
 * the parrotfish named in them, 107.1 g of 179.1 (four palenose, 107.1 g, and
 * one *Calotomus carolinus*, 72.0 g; seven unnamed, 139.9 g, and no
 * bullethead or redlip): 0.1652. Within the family the roi is taken to eat each
 * species as it meets it at the sizes it takes (no selection between them
 * was measured: five fish named).
 */
export const ROI_DIET: PredatorDiet = {
  predator: 'roi',
  prey: ['bullethead', 'palenose', 'redlip'],
  rationPerDay: ROI_RATION_PER_DAY,
  preyShare: (319.0 / 1154.9) * (107.1 / (107.1 + 72.0)),
};

/**
 * The game's parrotfish's share of the main islands' parrotfish (M126), by
 * weight: the bullethead's, the palenose's and since M128 the redlip's "B
 * from survey" over the six
 * species' Nadon 2017 gives (kg: *Calotomus carolinus* 38,102, p. 157;
 * *Chlorurus perspicillatus* 78,752, p. 163; *C. spilurus* 139,438, p. 169;
 * *Scarus dubius* 32,840, p. 175; *S. psittacus* 130,295, p. 181; *S.
 * rubroviolaceus* 623,532, p. 187), 0.8565 (M126's two, 0.2586): the
 * share of the parrotfish a predator eats that are the game's, where its
 * stomachs' parrotfish were not named by species, each species taken as it
 * is met (no selection between them was measured). A fish of the sizes the
 * fishery takes, not of the sizes a predator takes (`PREY_SIZE`): the
 * nearest measured. By the sizes a predator takes the share is the same to
 * a hundredth, 0.865 (MEASURED, M128: each of the six's stationary ages from
 * Nadon 2017's own growth, longevity, natural deaths, fishing and
 * selectivity, pp. 157 to 187, its ages from its curve's zero length, each
 * age's weight at `PREY_SIZE`'s chance over its weight at the survey's
 * selectivity, times its "B from survey"; the bullethead and the palenose
 * are most of the young, the redlip 17%).
 */
export const GAME_PARROTFISH_SHARE = (139_438 + 130_295 + 623_532) / (38_102 + 78_752 + 139_438 + 32_840 + 130_295 + 623_532);

/**
 * The jacks' diets (M126). Their stomachs: 81 ulua and 125 ʻōmilu with prey
 * that could be named, of the Northwestern islands ("Nearly all specimens
 * analyzed in this study were collected from the Northwestern Hawaiian
 * Islands", Sudekum et al. 1991, *Fish. Bull.* 89, Fig. 1, p. 494; a US
 * government work), parrotfish 13.4% of the ulua's named food by volume and
 * 19.0% of the ʻōmilu's ("Scaridae", Tables 3 and 4, pp. 501 and 502; "No
 * scarids could be identified to lower taxa", p. 507), taken by weight (a
 * fish's flesh as dense as the next's), the game's share of them
 * `GAME_PARROTFISH_SHARE`. The only Hawaiian stomachs of either served; the
 * main islands' unread. Two biases its authors name, the shares taken as
 * served: the filefish in both, "probably a result of an unusual population
 * explosion" in the years sampled, and the parrotfish's own, "biased by the
 * ease of identifying scarid dental plates" (p. 507). The food that could
 * not be named taken to be as the named was (assumed, M150's review: the
 * shares are of the named food alone, as the paper gives them). Their
 * rations: Weijerman, Fulton and Parrish's (2013, *PLoS ONE* 8: e63797, CC0)
 * consumption over biomass of the roving piscivores, from "empirical
 * relationships following Pauly [53] and Palomares and Pauly [54]" (p. 2):
 * Oʻahu's 4.46 a year, where the ulua is "69.4%" of their weight (Table S5;
 * text S1, S1.30), the ulua's; Kona's 5.86, where the ʻōmilu is "38.3%"
 * (Table S4), the ʻōmilu's: derived. Beside them Kona's benthic piscivores'
 * 4.73, where the roi is 63.5% of them, against the roi's measured 4.24 in
 * the tanks (`ROI_RATION_PER_DAY`, a year's). Their prey's sizes: the roi's
 * (`PREY_SIZE`), none read for a jack (assumed).
 */
export const ULUA_DIET: PredatorDiet = {
  predator: 'ulua',
  prey: ['bullethead', 'palenose', 'redlip'],
  rationPerDay: 4.46 / 365,
  preyShare: 0.134 * GAME_PARROTFISH_SHARE,
};
export const OMILU_DIET: PredatorDiet = {
  predator: 'omilu',
  prey: ['bullethead', 'palenose', 'redlip'],
  rationPerDay: 5.86 / 365,
  preyShare: 0.19 * GAME_PARROTFISH_SHARE,
};

/** The prey's chance by class, a step's, for a life: `PREY_SIZE` at each class's total length. Worked out once. */
const chanceByLife = new Map<FishLife, readonly (readonly number[])[]>();
export function sizeChances(life: FishLife): readonly (readonly number[])[] {
  let chances = chanceByLife.get(life);
  if (!chances) {
    chances = classesOf(life).map((step) => step.map((k: AgeClass) => (life.takenAtEverySize ? 1 : PREY_SIZE[Math.floor(k.tlMm / PREY_SIZE_BIN_MM)] ?? 0)));
    chanceByLife.set(life, chances);
  }
  return chances;
}

/** A stock as predation reads it: its life and its fish. */
export interface Stock {
  life: FishLife;
  fish: FishState;
}

/** An island's day of predation: the predator's need, the prey it met (A, kg weighed by chance) and the other prey (G). */
export interface Hunt {
  needKg: number;
  metKg: number;
  otherKg: number;
}

/** A predator's day on a sanctuary: each of its prey's daily chance of being eaten by it, by island and class (`FISH_ISLANDS` order), and each island's hunt. */
export interface Predation {
  by: FishSpeciesId;
  chances: ReadonlyMap<FishSpeciesId, readonly (readonly number[])[]>;
  hunts: readonly Hunt[];
}

/** A stock's weight on each island, kg, a day `step` thirtieths through its month. */
export function islandKg(stock: Stock, step: number): number[] {
  const classes = classesOf(stock.life)[step] as readonly AgeClass[];
  return stock.fish.islands.map((counts) => {
    let kg = 0;
    for (let c = 0; c < counts.length; c++) kg += (counts[c] as number) * (classes[c] as AgeClass).weightKg;
    return kg;
  });
}

/** A prey stock's kilograms met on each island, each class's weighed by its size's chance (`PREY_SIZE`), a day `step` thirtieths through its month. */
export function metKg(stock: Stock, step: number): number[] {
  const classes = classesOf(stock.life)[step] as readonly AgeClass[];
  const chance = sizeChances(stock.life)[step] as readonly number[];
  return stock.fish.islands.map((counts) => {
    let kg = 0;
    for (let c = 0; c < counts.length; c++) kg += (counts[c] as number) * (classes[c] as AgeClass).weightKg * (chance[c] as number);
    return kg;
  });
}

/**
 * A prey's classes' chances of being eaten in a day on each island: the
 * predator's need there over all the prey met (`met`, A) and its other prey
 * (`other`, G), times the class's size's chance, so that the prey eaten come
 * to the need's prey part (`preyPart`).
 */
export function eatenChances(life: FishLife, step: number, need: readonly number[], met: readonly number[], other: readonly number[]): number[][] {
  const chance = sizeChances(life)[step] as readonly number[];
  return need.map((n, i) => {
    const k = huntRate(n, met[i] as number, other[i] as number);
    const row = chance.slice();
    for (let c = 0; c < row.length; c++) row[c] = k * (row[c] as number);
    return row;
  });
}

/** The share of a predator's need its prey met make up against its other prey: A / G, at most all of it, none where none is met (above). */
export function preyPart(metKg: number, otherKg: number): number {
  return metKg > 0 ? Math.min(1, metKg / otherKg) : 0;
}

/** A predator's chance on each kilogram of prey met in a day: its need's prey part over the prey met. */
function huntRate(needKg: number, metKg: number, otherKg: number): number {
  return metKg > 0 ? (needKg * preyPart(metKg, otherKg)) / metKg : 0;
}

/**
 * The day's predation (above): on each island the predator's need, its prey
 * met and each prey class's chance of being eaten in the day.
 * `other` is each island's G (`FISH_ISLANDS` order).
 */
export function predation(diet: PredatorDiet, predator: Stock, prey: readonly Stock[], other: readonly number[], step: number): Predation {
  return hunted(diet, islandKg(predator, step), prey.map((s) => ({ life: s.life, metKg: metKg(s, step) })), other, step);
}

/** `predation` from the predator's kilograms on each island (`islandKg`) and each prey's met (`metKg`), which a day's hunts share (`reefPredation`). */
function hunted(diet: PredatorDiet, predatorKg: readonly number[], prey: readonly { life: FishLife; metKg: readonly number[] }[], other: readonly number[], step: number): Predation {
  const need = predatorKg.map((kg) => kg * diet.rationPerDay);
  const met = need.map(() => 0);
  for (const s of prey) s.metKg.forEach((kg, i) => { met[i] = (met[i] as number) + kg; });
  const chances = new Map<FishSpeciesId, number[][]>();
  for (const s of prey) chances.set(s.life.id, eatenChances(s.life, step, need, met, other));
  return { by: diet.predator, chances, hunts: need.map((n, i) => ({ needKg: n, metKg: met[i] as number, otherKg: other[i] as number })) };
}

/** A reef fish as a sanctuary steps it: its life, its balance's supply and first of January, and its streams' salt (`systems/fish.ts` holds the game's). */
export interface ReefSpecies {
  life: FishLife;
  supply: FishSupply;
  january: FishJanuary;
  salt: number;
}

/** The predators the game holds and what they eat: the roi (M84), the ulua and the ʻōmilu (M126). */
export const PREDATORS: readonly PredatorDiet[] = [ROI_DIET, ULUA_DIET, OMILU_DIET];

/**
 * The ulua's hunt on the day octopus (M129), apart from its hunt on the parrotfish: "Octopus" 12.5% of its stomachs'
 * named food by volume (Sudekum et al. 1991, Table 3, p. 501, 81 ulua with prey that could be named), taken by weight
 * as the parrotfish's are, every octopus in it the day octopus (assumed: the earlier report it quotes names both
 * *Octopus cyanea* and *O. ornatus* in the ulua's stomachs, p. 507, their split unmeasured). Its other prey G its own,
 * held in the octopus's table (`otherPreyIn`), so the octopus is balanced against it alone: the predation density
 * independent in its prey (`preyPart`), the ulua's two hunts take each its own share of its need. The ʻōmilu's octopus
 * (0.4% of its named food, Table 4) is not carried.
 */
export const ULUA_OCTOPUS_DIET: PredatorDiet = {
  predator: 'ulua',
  prey: ['octopus'],
  // The ulua's one ration, its hunts' together (review M149: typed twice, one could move without the other).
  rationPerDay: ULUA_DIET.rationPerDay,
  preyShare: 0.125,
  otherPreyIn: 'octopus',
};

/**
 * The jacks' hunts on the moano kea, the blue goatfish (M179), each apart from its hunt on the parrotfish as the ulua's
 * on the octopus is. Their goatfish: "Mullidae" 8.6% of the ulua's named food by volume (81 ulua, Sudekum et al. 1991,
 * Table 3, p. 501) and 17.7% of the ʻōmilu's (125 ʻōmilu, Table 4, p. 502), taken by weight as the parrotfish's are;
 * the blue goatfish's share of them its share of Maui Nui's goatfish by weight, 12.6% (`MOANOKEA_SURVEY.weightShare`,
 * MEASURED from NOAA's divers' counts), each species taken as it is met at every size, as the game's parrotfish are
 * (`GAME_PARROTFISH_SHARE`). The stomachs name it, "[Parupeneus cyclostomus]" 8.2% of the ulua's food and 3.5% of the
 * ʻōmilu's, each one fish by its frequency (1.2% of 81 ulua, 0.8% of 125 ʻōmilu, M179's reader), at the Northwestern
 * islands: taken as the jacks' share there, the jacks would need 157 t a year of Maui Nui's (MEASURED, M179, the jacks'
 * tables' weight at their rations), against its 21.6 t of the sizes taken, the year's mean; a check, not carried. Its other prey G each
 * jack's own in this hunt, held in the moano kea's table (`otherPreyIn`), so it is balanced against them alone. The
 * roi's goatfish, 40.3 g of the 1,154.9 g of prey in its stomachs (Dierking, Williams and Walsh 2009, Table 2), none
 * named to species, would hold the blue goatfish at its share of Maui Nui's goatfish at the sizes the roi takes, 1.2% by
 * weight (`MOANOKEA_SURVEY.roiShare`, MEASURED): 0.04% of the roi's need, not carried.
 */
export const ULUA_MOANOKEA_DIET: PredatorDiet = {
  predator: 'ulua',
  prey: ['moanokea'],
  rationPerDay: ULUA_DIET.rationPerDay,
  preyShare: 0.086 * MOANOKEA_SURVEY.weightShare,
  otherPreyIn: 'moanokea',
};
export const OMILU_MOANOKEA_DIET: PredatorDiet = {
  predator: 'omilu',
  prey: ['moanokea'],
  rationPerDay: OMILU_DIET.rationPerDay,
  preyShare: 0.177 * MOANOKEA_SURVEY.weightShare,
  otherPreyIn: 'moanokea',
};

/** Every hunt a day reckons: the parrotfish's (`PREDATORS`), the octopus's (M129) and the moano kea's (M179). */
export const REEF_DIETS: readonly PredatorDiet[] = [...PREDATORS, ULUA_OCTOPUS_DIET, ULUA_MOANOKEA_DIET, OMILU_MOANOKEA_DIET];

/**
 * A sanctuary's day of predation, its stocks aged: each predator's, on its
 * prey as they stand, the other prey its balance's (`FishSupply.otherPreyKg`; a second hunt's, on a prey of
 * its own balance, that prey's table's `predatorOtherPreyKg`: the ulua's on the octopus, M129, and the jacks' on the moano kea, M179).
 */
export function reefPredation(species: readonly ReefSpecies[], fish: ReefFish, epochDay: number, diets: readonly PredatorDiet[] = REEF_DIETS): Predation[] {
  const step = monthStep(epochDay);
  const of = (id: FishSpeciesId): ReefSpecies => {
    const sp = species.find((x) => x.life.id === id);
    if (!sp) throw new Error(`no reef species ${id}`);
    return sp;
  };
  // Each stock's kilograms worked out once for the day, however many hunts read them (the ulua hunts three times, the parrotfish are met by three).
  const kgOf = new Map<FishSpeciesId, number[]>();
  const metOf = new Map<FishSpeciesId, number[]>();
  const once = (known: Map<FishSpeciesId, number[]>, id: FishSpeciesId, kg: (stock: Stock) => number[]): number[] => {
    let got = known.get(id);
    if (!got) {
      got = kg({ life: of(id).life, fish: fish[id] });
      known.set(id, got);
    }
    return got;
  };
  return diets.map((diet) => {
    const own = of(diet.predator);
    const other = diet.otherPreyIn ? of(diet.otherPreyIn).supply.predatorOtherPreyKg?.[diet.predator] : own.supply.otherPreyKg;
    if (!other) throw new Error(`${diet.predator}'s balance gives no other prey for its hunt on ${diet.prey.join(', ')}`);
    return hunted(diet, once(kgOf, diet.predator, (s) => islandKg(s, step)), diet.prey.map((id) => ({ life: of(id).life, metKg: once(metOf, id, (s) => metKg(s, step)) })), other, step);
  });
}

/** Several predators' chances on a prey's classes, by island, added in their order (`PREDATORS`'s); one predator's its own. The balance adds them as the tick does (`scripts/reefFish.ts`). */
export function addedChances(each: readonly (readonly (readonly number[])[])[]): readonly (readonly number[])[] {
  const first = each[0] as readonly (readonly number[])[];
  if (each.length === 1) return first;
  return first.map((row, i) => {
    const sum = row.slice();
    for (let k = 1; k < each.length; k++) {
      const add = (each[k] as readonly (readonly number[])[])[i] as readonly number[];
      for (let c = 0; c < sum.length; c++) sum[c] = (sum[c] as number) + (add[c] as number);
    }
    return sum;
  });
}

/** A predator's part of a prey's dead eaten on an island (M126): its chance over all the chances on it there, the same at every size (`PREY_SIZE` is every predator's). */
export interface EatenBy {
  predator: FishSpeciesId;
  share: number;
}

/**
 * A prey's day of being eaten (M126): its classes' chances by island, every
 * predator's added, and on each island each predator's part of them; none
 * where nothing eats it. One predator's chances are its own, to the bit.
 */
export function eatenBy(hunts: readonly Predation[], id: FishSpeciesId): { chances: readonly (readonly number[])[]; by: readonly (readonly EatenBy[])[] } | null {
  const eaters = hunts.filter((h) => h.chances.has(id));
  const first = eaters[0];
  if (!first) return null;
  const chances = addedChances(eaters.map((h) => h.chances.get(id) as readonly (readonly number[])[]));
  // Each one's part on an island: its chance's factor on every class there (`huntRate`).
  const by = first.hunts.map((_, i) => {
    const rate = eaters.map((h) => {
      const hunt = h.hunts[i] as Hunt;
      return huntRate(hunt.needKg, hunt.metKg, hunt.otherKg);
    });
    const all = rate.reduce((a, r) => a + r, 0);
    return eaters.map((h, k) => ({ predator: h.by, share: all > 0 ? (rate[k] as number) / all : 1 / eaters.length }));
  });
  return { chances, by };
}

/**
 * A sanctuary's reef fish, a day, in place: every stock a month older on a
 * month's first (`ageFish`), the day's predation reckoned on them as they
 * stand, then each species' day (`liveFish`), its chances of being eaten,
 * every predator's added (`eatenBy`), among its deaths, on the draws
 * `drawsOf` gives it. With `report`, each stock's island day is told to it,
 * each predator's part of its dead eaten (none where nothing eats it) and a
 * predator's hunt with it. `diets` are the predators' (the game's, `PREDATORS`). `fishingOf` gives a species' fishing on
 * each island where it is not the stock's own (M165, the sanctuary's protection; M170, its size and bag limits); none,
 * every island its stock's.
 */
export function stepReef(species: readonly ReefSpecies[], fish: ReefFish, epochDay: number, drawsOf: (sp: ReefSpecies) => FishDraws,
  report?: (sp: ReefSpecies, day: IslandDay, eaters: readonly EatenBy[], hunt: Hunt | undefined) => void, diets: readonly PredatorDiet[] = REEF_DIETS,
  onHunts?: (hunts: readonly Predation[]) => void, fishingOf?: (sp: ReefSpecies) => readonly IslandFishing[] | undefined): void {
  for (const sp of species) ageFish(sp.life, fish[sp.life.id], epochDay);
  const hunts = reefPredation(species, fish, epochDay, diets);
  // The tagged animals' day (M148): their stocks aged and the hunts reckoned, before any lives.
  onHunts?.(hunts);
  for (const sp of species) {
    const id = sp.life.id;
    const eaten = eatenBy(hunts, id);
    const own = hunts.find((h) => h.by === id);
    liveFish(sp.life, fish[id], epochDay, sp.supply, drawsOf(sp),
      report ? (day) => report(sp, day, eaten?.by[day.island] ?? [], own?.hunts[day.island]) : undefined, eaten?.chances, fishingOf?.(sp));
  }
}
